import { formatAddress } from '@/helpers/formatAddress';
import { formatPhoneNumber } from '@/helpers/formatPhoneNumber';
import {
  isPackageEditable,
  PACKAGE_IN_REVIEW_QUEUE_ALERT_MESSAGE,
  shouldShowPackageUnderReviewAlert,
} from '@/helpers/packageEditability';
import usePermissions from '@/hooks/usePermissions';
import { useToastSuccessSavedPackageMessage } from '@/hooks/useSavedPackageMessage';
import { useGetDocumentsQuery } from '@/services/documents';
import {
  useSaveInstallHVACPackageMutation,
  useSubmitInstallHVACPackageMutation,
} from '@/services/installHVACPackage';
import { useGetOrganizationDetailsQuery } from '@/services/organizations';
import { useGetClassificationsQuery } from '@/services/productCatalog';
import { Alert, DateInput, toast, useOpenClose } from '@palmetto/palmetto-components';
import { Field, Formik, FormikValues } from 'formik';
import { DateTime } from 'luxon';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import * as yup from 'yup';

import {
  Applicant,
  ApplicantType,
  DeepPartial,
  HVACAccessoryItem,
  HVACAccessoryType,
  HVACEquipmentItem,
  HVACEquipmentType,
  HVACInstallPackageFileCategory,
  HVACSystem,
  HVACSystemDesign,
  HVACZoningItem,
  InstallHVACPackage as IInstallHVACPackage,
  InstallationPackagePhotoCategory,
  InstallDocumentTypes,
  MilestoneType,
  QueueStatus,
  QueueType,
  RequirementFlagSettingsType,
  UserPermissions,
  ZoningType,
} from 'types';

import { convertToISODateTimeString, DATE_FORMAT } from '../../helpers/dates';
import { useGetAccountQuery } from '../../services/accounts';
import { isErrorWithData } from '../../services/helpers';
import { useGetQueueByAccountIdAndTypeQuery } from '../../services/queues';
import { ReviewPackageActions } from '../../types/ReviewPackage';
import { formatContractSkuTermMappings } from '../../utils/warranties';
import { ActionsList } from '../ActionsList';
import { useAuth } from '../auth/authProvider';
import { isInternalUser } from '../auth/RequireIsInternalUser';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import { EnhancedFormikForm } from '../EnhancedFormikForm';
import FormSection from '../Forms/FormSection';
import { MainContainer } from '../MainContainer';
import { ContractDocumentSection } from '../MilestonePackagePartials/ContractDocumentSection';
import { Footer } from '../MilestonePackagePartials/Footer';
import { AttestationSection } from '../MilestonePackagePartials/hvac/AttestationSection';
import { HVACInstallContractInfo } from '../MilestonePackagePartials/hvac/HVACInstallContractInfo';
import { InstallationDocuments } from '../MilestonePackagePartials/hvac/InstallationDocuments';
import { InstallContract } from '../MilestonePackagePartials/hvac/InstallContract';
import { InstallPhotos } from '../MilestonePackagePartials/hvac/InstallPhotos';
import { LoadCalculationHVAC } from '../MilestonePackagePartials/hvac/LoadCalculation';
import { SystemPhotos } from '../MilestonePackagePartials/hvac/SystemPhotos';
import WarrantyRegistration from '../MilestonePackagePartials/hvac/WarrantyRegistration';
import { QueueBanner } from '../MilestonePackagePartials/QueueBanner';
import { NotesDrawer } from '../NotesDrawer/NotesDrawer';
import { HVACAttestationModal } from '../ProductionValueCheck/HvacAttestationModal';
import { FlagBox } from '../QueueFlags/FlagBox';
import { QueueFlagsContext, QueueFlagsProvider } from '../QueueFlags/QueueFlagsContext';
import { SkeletonBox } from '../SkeletonBox';
import { AhriCertificateSection } from '../SystemDesign/hvac/AhriCertificateSection';
import { SystemSection } from '../SystemDesign/hvac/SystemSection';

import { validateWarranties } from './InstallHVACPackage.utils';

interface InstallPackageProps {
  canSubmitInstallPackage: boolean;
}

function isEquipmentSerialNumberRequired(categories: any[], productType: HVACEquipmentType | HVACAccessoryType) {
  if (productType === HVACEquipmentType.other) {
    return false;
  }

  const category = categories.find((obj) => {
    const productTypeAttr = obj.defaultAttributes.find(
      (attr: any) => attr.key === 'lightreach-product-type' && attr.value === productType,
    );
    return productTypeAttr;
  });

  if (category) {
    const serialAttr = category.defaultAttributes.find(
      (attr: any) => attr.key === 'lightreach-serial-number-required',
    );
    return serialAttr?.value === 'true';
  }

  return false;
}

const InstallHVACPackageSchema = yup.object().shape({
  systemInstallDate: yup.string().required('System Install Date is a required field.'),
  permitAttestationCheckbox: yup.boolean().oneOf([true], 'Permit Attestation is a required field.'),
});

const permitAttestLabel =
  'I attest that I have or will apply for any and all necessary permits with the local authority having jurisdiction';

export const formatInstallHVACPackagesData = (
  values: FormikValues,
  systemDesign: HVACSystemDesign,
): DeepPartial<IInstallHVACPackage> => {
  return {
    systemDesign: {
      isPreliminary: false,
      systems: values?.systems?.map((system: HVACSystem, systemIndex: number) => {
        const originalZoningItemsRaw = systemDesign.systems?.[systemIndex]?.zoning?.items;
        const originalZoningItems = Array.isArray(originalZoningItemsRaw)
          ? originalZoningItemsRaw.filter((zoningItem) => zoningItem.type === HVACAccessoryType.zoning)
          : [];
        const splitAccessories = system?.accessories?.items?.reduce(
          (acc: { accessories: HVACAccessoryItem[]; zoning: HVACZoningItem[] }, item: HVACAccessoryItem) => {
            if ([ZoningType.bypassDamper, ZoningType.zoneBoard, ZoningType.zoneDamper].includes(item.type as any)) {
              acc.zoning.push(item as HVACZoningItem);
            } else {
              acc.accessories.push(item);
            }
            return acc;
          },
          { accessories: [] as HVACAccessoryItem[], zoning: [] as HVACZoningItem[] },
        );
        const zoningItems = [...originalZoningItems, ...(splitAccessories?.zoning || [])];
        return {
          systemCategory: system.systemCategory,
          conditionedArea: system.conditionedArea,
          name: system.name,
          ahriNumber: system.ahriNumber,
          manufacturer: system.manufacturer,
          // Ids only. The server reads both records and snapshots their names.
          systemManufacturer: system.systemManufacturer && {
            offAvl: system.systemManufacturer.offAvl,
            offAvlName: system.systemManufacturer.offAvlName,
            manufacturerId: system.systemManufacturer.manufacturerId,
            brandId: system.systemManufacturer.brandId,
          },
          equipment: {
            totalCost: system.equipment.totalCost,
            items: system.equipment.items.map((item) => ({
              type: item.type,
              name: item.name || 'unknown',
              manufacturer: item.manufacturer || 'unknown',
              model: item.model || 'unknown',
              quantity: item.quantity,
              productId: item.productId,
              serialNumbers: item.serialNumbers,
              efficiencies: item.efficiencies,
              size: item.size,
            })),
          },
          accessories: splitAccessories?.accessories?.length
            ? {
                totalCost: system?.accessories?.totalCost,
                items: splitAccessories.accessories.map((item) => ({
                  type: item.type,
                  name: item.name || 'unknown',
                  manufacturer: item.manufacturer || 'unknown',
                  model: item.model || 'unknown',
                  quantity: item.quantity,
                  productId: item.productId,
                  serialNumbers: item.serialNumbers,
                  airFilterType: item.airFilterType,
                })),
              }
            : undefined,
          zoning: zoningItems?.length
            ? {
                totalCost: system?.zoning?.totalCost,
                items: zoningItems.map((item) => ({
                  type: item.type,
                  name: item.name || 'unknown',
                  manufacturer: item.manufacturer || 'unknown',
                  model: item.model || 'unknown',
                  quantity: item.quantity,
                  productId: item.productId,
                  serialNumbers: item.serialNumbers,
                })),
              }
            : undefined,
          sitePrep: system?.sitePrep
            ? {
                totalCost: system.sitePrep.totalCost,
                items: (system.sitePrep?.items || []).map((item) => ({
                  name: item.name,
                })),
              }
            : undefined,
          discounts: system?.discounts
            ? {
                totalCost: system.discounts.totalCost,
                items: (system.discounts?.items || []).map((item) => ({
                  name: item.name,
                })),
              }
            : undefined,
          efficiencies: {
            seer2: system.efficiencies?.seer2 || '',
            hspf2: system.efficiencies?.hspf2 || '',
          },
        };
      }),
    },
    systemInstallDate: values.systemInstallDate
      ? { palmetto: convertToISODateTimeString(values.systemInstallDate) }
      : undefined,
    permitAttestation: values.permitAttestationCheckbox
      ? { attestmentText: values.permitAttestationCheckbox ? permitAttestLabel : '' }
      : undefined,
    workOrderTotalPrice: values.workOrderTotalPrice || undefined,
  };
};

const flattenSystemItems = (items: HVACEquipmentItem[] | HVACAccessoryItem[] | HVACZoningItem[]) => {
  const newItems: any[] = [];

  for (const item of items) {
    if (item.quantity > 1) {
      for (let i = 0; i < item.quantity; i++) {
        newItems.push({
          ...item,
          quantity: 1,
        });
      }
    } else {
      newItems.push({ ...item });
    }
  }
  return newItems;
};

const documentsPreviouslyUploadedToHvacInstallation = [
  InstallDocumentTypes.proofOfLoadHVAC,
  InstallDocumentTypes.fsmChecklist,
  InstallDocumentTypes.measureQuickSystemVitals,
  InstallDocumentTypes.bluetoothHVACTool,
  InstallationPackagePhotoCategory.projectSite,
  InstallationPackagePhotoCategory.indoorSystem,
  InstallationPackagePhotoCategory.outdoorSystem,
  InstallationPackagePhotoCategory.zoningSystem,
  InstallationPackagePhotoCategory.thermostat,
  InstallationPackagePhotoCategory.accessories,
];

export const InstallHVACPackage = ({ canSubmitInstallPackage }: InstallPackageProps) => {
  const navigate = useNavigate();
  const { claims } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const permissions = usePermissions();
  const isLightReachQCTech = permissions.includes(UserPermissions.lightReachQCTech);
  const userOnlyHasReadPermissions = permissions?.every((permission) => permission.includes(UserPermissions.reader));
  const { id = '', action } = useParams<{ id: string; action: ReviewPackageActions }>();
  const { data: queue, isLoading: isQueueLoading } = useGetQueueByAccountIdAndTypeQuery(
    {
      accountId: id,
      type: QueueType.installPackage,
    },
    { skip: !id },
  );
  const { getFlagsByFlagType } = useContext(QueueFlagsContext);
  const checkboxCategories = useMemo(() => Object.values(HVACInstallPackageFileCategory), []);
  const showCheckboxes = isInternalUser(claims, true);

  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(id, { skip: !id });
  const { data: organization } = useGetOrganizationDetailsQuery(
    { alias: account?.organizationId || '' },
    { skip: !account?.organizationId || !isFinCoUser },
  );
  const primaryApplicant = account?.applicants.find(
    (applicant: Applicant) => applicant.type === ApplicantType.primary,
  );
  const systemInstallDate = (values: any): Date | null => {
    return values.systemInstallDate ? DateTime.fromFormat(values.systemInstallDate, DATE_FORMAT).toJSDate() : null;
  };

  const handleDateChange = useCallback((date: Date | [Date, Date] | null, setFieldValue: any) => {
    if (!date || !(date instanceof Date)) return;
    setFieldValue('systemInstallDate', DateTime.fromJSDate(date).toUTC().toFormat(DATE_FORMAT));
  }, []);

  const [warrantiesConfirmed, setWarrantiesConfirmed] = useState(action !== ReviewPackageActions.review);

  const minDate = DateTime.fromISO('2022-01-01').toJSDate();
  const maxDate = DateTime.local().endOf('day').toJSDate();
  const [triggerSubmit] = useSubmitInstallHVACPackageMutation();
  const [triggerSave] = useSaveInstallHVACPackageMutation();

  const { data: classifications } = useGetClassificationsQuery({ path: 'hvac' });
  const {
    isOpen: isAttestationOpen,
    handleOpen: setAttestationSubmissionOpen,
    handleClose: setAttestationSubmissionClose,
  } = useOpenClose();
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [userAttestation, setUserAttestation] = useState(false);
  const isLoading = isAccountLoading || isQueueLoading;
  const { data: wasADocumentPreviouslyUploadedToHvacInstallation } = useGetDocumentsQuery(
    {
      accountId: id,
      documentTypeFilter: documentsPreviouslyUploadedToHvacInstallation,
    },
    { skip: !id },
  );

  const { data: ahriCertificates } = useGetDocumentsQuery(
    {
      accountId: id,
      documentTypeFilter: [InstallDocumentTypes.ahriCertificate],
    },
    { skip: !id },
  );

  const customerContractFlags = getFlagsByFlagType(RequirementFlagSettingsType.customerContract);
  const hvacInstallationPhotosFlag = getFlagsByFlagType(RequirementFlagSettingsType.hvacInstallationPhotos);
  const showInstallPhotosForInitialSubmission =
    !wasADocumentPreviouslyUploadedToHvacInstallation?.length &&
    !isFinCoUser &&
    (!queue || (queue && queue.status === QueueStatus.submitted));
  const showInstallPhotosForEPCUser =
    !isFinCoUser && (hvacInstallationPhotosFlag?.length || (queue && queue.status === QueueStatus.submitted));
  const canEditPackage = isPackageEditable({ queue, isFinCoUser });
  const showUnderReviewAlert = shouldShowPackageUnderReviewAlert({
    queue,
    isFinCoUser,
  });

  const triggerSuccessMessage = useToastSuccessSavedPackageMessage(id, MilestoneType.install);

  const VALIDATION_ERROR = 'ValidationError';

  useEffect(() => {
    if (!action || !ReviewPackageActions[action]) {
      navigate(`/accounts/${id}`, { replace: true });
    }
    if (action === ReviewPackageActions.review && (!isFinCoUser || !isLightReachQCTech)) {
      if (queue) {
        navigate(`/accounts/${id}/installation-package/edit`, { replace: true });
      } else {
        navigate(`/accounts/${id}/installation-package/create`, { replace: true });
      }
    }
  }, [id, navigate, action, isFinCoUser, isLightReachQCTech, queue]);

  const accountInitialValues = useMemo(() => {
    if (!account || !classifications || !classifications.length) {
      return {};
    }
    const systems = account?.systemDesign?.systems.reduce((acc: HVACSystem[], system: HVACSystem) => {
      const equipment = flattenSystemItems(system.equipment.items).map((item) => {
        const isSerialNumberRequired = isEquipmentSerialNumberRequired(classifications, item.type);
        const serialNumbers =
          item.serialNumbers && item.serialNumbers.length
            ? item.serialNumbers
            : item.type !== HVACEquipmentType.other
              ? Array(item.quantity).fill('')
              : [''];
        return Object.assign(item, { isSerialNumberRequired, serialNumbers });
      });
      const accessories = flattenSystemItems(system.accessories?.items || []).map((item) => {
        const isSerialNumberRequired = isEquipmentSerialNumberRequired(classifications, item.type);
        const serialNumbers =
          item.serialNumbers && item.serialNumbers.length ? item.serialNumbers : Array(item.quantity).fill('');
        return Object.assign(item, { isSerialNumberRequired, serialNumbers });
      });

      const zoning: any[] = [];
      const zoningItems = system?.zoning?.items ?? [];
      const zoningReduction = zoningItems.reduce(
        (acc, item) => {
          if (item.type === HVACAccessoryType.zoning) {
            acc.quantity += item.quantity;
          }
          if (item.type === ZoningType.zoneDamper) {
            acc.dampers.push(item);
          } else if (item.type === ZoningType.zoneBoard) {
            acc.boards.push(item);
          } else if (item.type === ZoningType.bypassDamper) {
            acc.bypassDampers.push(item);
          }
          return acc;
        },
        {
          quantity: 0,
          boards: [] as HVACZoningItem[],
          dampers: [] as HVACZoningItem[],
          bypassDampers: [] as HVACZoningItem[],
        },
      );

      if (zoningReduction?.quantity > 0) {
        if (!zoningReduction.boards.length) {
          zoning.push({
            type: ZoningType.zoneBoard,
            isSerialNumberRequired: false,
            quantity: 1,
            serialNumbers: [''],
            manufacturer: '',
            model: '',
          });
        } else {
          zoning.push(...zoningReduction.boards);
        }

        const damperQuantity = zoningReduction.quantity + 1;
        if (zoningReduction.dampers.length < damperQuantity) {
          zoning.push(...zoningReduction.dampers);
          const missingZoneDamperCount = damperQuantity - zoningReduction.dampers.length;
          for (let i = 0; i < missingZoneDamperCount; i++) {
            zoning.push({
              type: ZoningType.zoneDamper,
              isSerialNumberRequired: false,
              quantity: 1,
              serialNumbers: [''],
              manufacturer: '',
              model: '',
            });
          }
        } else {
          zoning.push(...zoningReduction.dampers);
        }

        if (!zoningReduction.bypassDampers.length) {
          zoning.push({
            type: ZoningType.bypassDamper,
            isSerialNumberRequired: false,
            quantity: 1,
            serialNumbers: [''],
            manufacturer: '',
            model: '',
          });
        } else {
          zoning.push(...zoningReduction.bypassDampers);
        }
      }

      const totalCost =
        (system.equipment.totalCost || 0) + (system.accessories?.totalCost || 0) + (system.zoning?.totalCost || 0);

      const updatedSystem = {
        ...system,
        equipment: {
          ...system.equipment,
          totalCost,
          items: equipment,
        },
        accessories:
          accessories.length > 0 || zoning.length > 0
            ? {
                ...system.accessories,
                totalCost: 0,
                items: [...accessories, ...zoning],
              }
            : { totalCost: 0, items: [] },
        efficiencies: system?.efficiencies || {
          seer2: '',
          hspf2: '',
        },
        zoning: {
          totalCost: 0,
          items: [],
        },
      } as HVACSystem;
      acc.push(updatedSystem);
      return acc;
    }, [] as HVACSystem[]);
    return {
      systemInstallDate: account?.systemInstallDate?.palmetto
        ? DateTime.fromISO(account.systemInstallDate.palmetto).toUTC().toFormat(DATE_FORMAT)
        : '',
      systems,
      workOrderTotalPrice: account?.workOrderTotalPrice,
    };
  }, [account, classifications]);

  const canSubmitReview = action === ReviewPackageActions.review && queue;
  const initialValues = {
    ...accountInitialValues,
  };

  const validateAhriCertificates = (data: FormikValues, targetStatus?: QueueStatus): void => {
    if (
      isFinCoUser &&
      action === ReviewPackageActions.review &&
      targetStatus === QueueStatus.approved &&
      (!ahriCertificates?.length || ahriCertificates?.length < data.systems?.length)
    ) {
      const validationError = new Error('AHRI Certificates are required for each system');
      validationError.name = VALIDATION_ERROR;
      throw validationError;
    }
  };

  const validateManufacturerFields = (data: FormikValues, targetStatus?: QueueStatus): void => {
    if (
      isFinCoUser &&
      action === ReviewPackageActions.review &&
      targetStatus &&
      ![QueueStatus.onHold, QueueStatus.rejected].includes(targetStatus) &&
      !data.systems.every((system: HVACSystem) => !!system.manufacturer)
    ) {
      const validationError = new Error('Manufacturer is required for each system');
      validationError.name = VALIDATION_ERROR;
      throw validationError;
    }
  };

  const validateInstallPackage = (data: FormikValues, targetStatus?: QueueStatus): void => {
    validateAhriCertificates(data, targetStatus);
    validateManufacturerFields(data, targetStatus);
    validateWarranties({ isFinCoUser, action, warrantiesConfirmed, targetStatus });
  };

  const handleSubmit = async (values: FormikValues, { setSubmitting }: any) => {
    const formValues = formatInstallHVACPackagesData(values, account.systemDesign);

    if (!userAttestation) {
      setAttestationSubmissionOpen();
      return;
    }
    try {
      setSubmitting(true);
      await triggerSubmit({ accountId: id, installPackage: formValues as IInstallHVACPackage }).unwrap();
      toast.success('Install Package Submitted');
      navigate(`/accounts/${id}`);
    } catch (e: any) {
      toast.error(e?.data?.message || 'Error submitting install package');
    } finally {
      setSubmitting(false);
      setUserAttestation(false);
      setAttestationSubmissionClose();
    }
  };

  const handleSave = async ({
    accountId,
    data,
    targetStatus,
  }: {
    accountId: string;
    data: FormikValues;
    targetStatus?: QueueStatus;
  }) => {
    try {
      if (targetStatus) {
        validateInstallPackage(data, targetStatus);
      }

      const formValues = formatInstallHVACPackagesData(data, account.systemDesign);
      await triggerSave({ accountId, savePackage: formValues }).unwrap();
      triggerSuccessMessage();
    } catch (e) {
      console.error(e);
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        throw new Error(errorMessage);
      } else if (e instanceof Error && e.name === VALIDATION_ERROR) {
        throw e;
      } else {
        console.error(e);
        throw new Error('Error saving Install Package');
      }
    }
  };

  const queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser: boolean =
    !isFinCoUser &&
    queue &&
    (queue.status === QueueStatus.conditionallyApproved || queue.status === QueueStatus.approved);

  const showAhriSection = isFinCoUser || queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser;

  if (isLoading) {
    return (
      <MainContainer>
        <FormSection title="Loading...">
          <SkeletonBox height="25px" width="100" />
        </FormSection>
      </MainContainer>
    );
  }

  const attestationModalText =
    'By submitting this package, I attest that all equipment listed is accurate to the equipment installed for the customer, and that all equipment is working to manufacturer specifications.';

  return (
    <>
      <QueueFlagsProvider queueId={queue?.id}>
        <NotesDrawer isOpen={isNotesOpen} setIsOpen={setIsNotesOpen} queueType={QueueType.installPackage} />
        <MainContainer>
          <Formik
            initialValues={initialValues}
            validationSchema={InstallHVACPackageSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ setFieldValue, values, setSubmitting, isSubmitting, touched, errors }) => (
              <>
                <EnhancedFormikForm>
                  <ActionsList
                    milestoneType={MilestoneType.install}
                    accountId={queue?.account?.id}
                    canSubmitPackage={canSubmitInstallPackage}
                    isLoading={isLoading}
                    isFirstSubmission={!queue}
                    canSubmitReview={canSubmitReview}
                    onSave={handleSave}
                    disabled={queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser || !canEditPackage}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    queue={queue}
                  />

                  {queue && (queue.status === QueueStatus.rejected || isFinCoUser) ? (
                    <QueueBanner
                      setIsNotesOpen={setIsNotesOpen}
                      queueType={QueueType.installPackage}
                      isFinCoUser={isFinCoUser}
                    />
                  ) : null}
                  {showUnderReviewAlert ? (
                    <Alert
                      variant="warning"
                      title="Install Package In Review Queue"
                      message={PACKAGE_IN_REVIEW_QUEUE_ALERT_MESSAGE}
                      hasIcon
                    />
                  ) : null}
                  {action === ReviewPackageActions.review && !canSubmitReview ? (
                    <Alert
                      variant="warning"
                      title="Install Package Not Submitted"
                      message="This install package is not ready to be reviewed yet. It has not been submitted."
                      hasIcon
                    />
                  ) : null}
                  {(isFinCoUser || (customerContractFlags?.length || 0) > 0) && (
                    <ContractDocumentSection showSeparatorBorder={true} />
                  )}
                  <FormSection title="System Install Date">
                    <FlagBox
                      dataKey="System Install Date"
                      baseCategory="systemInstallDate"
                      flagType={RequirementFlagSettingsType.systemInstallDateHVAC}
                      displayWithNoQueue
                    >
                      <Field
                        label=""
                        name="systemInstallDate"
                        id="systemInstallDate"
                        dateFormat={DATE_FORMAT}
                        width="2xl"
                        value={values.systemInstallDate}
                        component={DateInput}
                        datePickerProps={{
                          maxDate,
                          minDate,
                          onChange: (date: Date | [Date, Date] | null) => handleDateChange(date, setFieldValue),
                          openToDate: systemInstallDate(values) ?? undefined,
                        }}
                        textInputProps={{
                          onClear: () => setFieldValue('systemInstallDate', ''),
                        }}
                        isDisabled={isSubmitting || !canEditPackage}
                        error={touched.systemInstallDate && (errors.systemInstallDate as string)}
                      />
                    </FlagBox>
                  </FormSection>
                  {isFinCoUser && (
                    <FormSection title="Customer Information">
                      <table>
                        <tbody>
                          {queue?.account?.payeeContractor?.name && (
                            <tr>
                              <td>Contractor:</td>
                              <td>
                                <Link to={`/settings/organizations/${queue.account.payeeContractor.alias}`}>
                                  {queue.account.payeeContractor.name}
                                </Link>
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td>Partner Licensed Org:</td>
                            <td>
                              <Link to={`/settings/organizations/${account?.licensedOrganizationId}`}>
                                {account?.licensedOrganizationId?.toUpperCase() ?? ''}
                              </Link>
                            </td>
                          </tr>
                          <tr>
                            <td>Partner Org:</td>
                            <td>
                              <Link to={`/settings/organizations/${account?.organizationId}`}>
                                {account?.organizationId?.toUpperCase() ?? ''}
                              </Link>
                            </td>
                          </tr>
                          <tr>
                            <td>Primary Contact Details:</td>
                            <td>
                              {primaryApplicant?.firstName} {primaryApplicant?.lastName}
                              {' - '}
                              {primaryApplicant?.email}
                              {' - '}
                              {primaryApplicant?.phoneNumber ? formatPhoneNumber(primaryApplicant.phoneNumber) : ''}
                            </td>
                          </tr>
                          <tr>
                            <td>Property Address:</td>
                            <td>{formatAddress(account?.address)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </FormSection>
                  )}

                  {showInstallPhotosForInitialSubmission ? (
                    <InstallPhotos
                      showCheckboxes={showCheckboxes}
                      checkboxCategories={checkboxCategories}
                      isPackageEditable={canEditPackage}
                    />
                  ) : (
                    <>
                      {(isFinCoUser || showInstallPhotosForEPCUser) && (
                        <InstallPhotos
                          showCheckboxes={showCheckboxes}
                          checkboxCategories={checkboxCategories}
                          isPackageEditable={canEditPackage}
                        />
                      )}
                      <InstallContract
                        showCheckboxes={showCheckboxes}
                        checkboxCategories={checkboxCategories}
                        isPackageEditable={canEditPackage}
                      />
                      <LoadCalculationHVAC
                        showCheckboxes={showCheckboxes}
                        checkboxCategories={checkboxCategories}
                        isPackageEditable={canEditPackage}
                      />
                      {action === ReviewPackageActions.review && (
                        <FormSection>
                          <HVACInstallContractInfo
                            isSubmitting={isSubmitting}
                            isLoading={isLoading}
                            userOnlyHasReadPermissions={userOnlyHasReadPermissions}
                          />
                        </FormSection>
                      )}
                      <InstallationDocuments
                        showCheckboxes={showCheckboxes}
                        checkboxCategories={checkboxCategories}
                        isPackageEditable={canEditPackage}
                      />
                      <SystemPhotos
                        showCheckboxes={showCheckboxes}
                        checkboxCategories={checkboxCategories}
                        isPackageEditable={canEditPackage}
                      />
                    </>
                  )}
                  <SystemSection
                    isInstallPackage
                    isPreliminary={account?.systemDesign?.isPreliminary}
                    isFinCoUser={isFinCoUser}
                    action={action}
                    isPackageEditable={canEditPackage}
                    showAhriSection={showAhriSection}
                  />
                  {showAhriSection && (
                    <AhriCertificateSection
                      showCheckboxes={showCheckboxes}
                      checkboxCategories={checkboxCategories}
                      isPackageEditable={canEditPackage}
                      showUploadButton={isFinCoUser && action === ReviewPackageActions.review}
                    />
                  )}
                  {isFinCoUser && action === ReviewPackageActions.review && (
                    <>
                      <WarrantyRegistration
                        onValidityChange={setWarrantiesConfirmed}
                        isPackageEditable={canEditPackage}
                      />
                    </>
                  )}
                  {isFinCoUser && organization && (
                    <FormSection title="Extended Warranty Information">
                      {organization.extendedWarrantyDealerInformationOrganization && (
                        <Alert
                          variant="info"
                          message={`Inherited from ${organization.extendedWarrantyDealerInformationOrganization}`}
                          hasIcon
                        />
                      )}
                      {organization.extendedWarrantyDealerInformation?.length ? (
                        <table>
                          <thead>
                            <tr>
                              <th style={{ textAlign: 'left', paddingRight: '16px' }}>Provider</th>
                              <th style={{ textAlign: 'left', paddingRight: '16px' }}>Dealer License #</th>
                              <th style={{ textAlign: 'left', paddingRight: '16px' }}>Contract SKU Mappings</th>
                            </tr>
                          </thead>
                          <tbody>
                            {organization.extendedWarrantyDealerInformation.map((ewdi: any, index: number) => (
                              <tr key={index}>
                                <td style={{ paddingRight: '16px' }}>{ewdi.warrantyProvider}</td>
                                <td style={{ paddingRight: '16px' }}>{ewdi.dealerLicenseNumber || '-'}</td>
                                <td style={{ paddingRight: '16px' }}>{formatContractSkuTermMappings(ewdi)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <Alert
                          variant="warning"
                          message="No extended warranty dealer information configured for this account."
                          hasIcon
                        />
                      )}
                    </FormSection>
                  )}
                  {action !== ReviewPackageActions.review && (
                    <AttestationSection
                      permitAttestLabel={permitAttestLabel}
                      setFieldValue={setFieldValue}
                      values={values}
                      isSubmitting={isSubmitting}
                      touched={touched}
                      errors={errors}
                      isPackageEditable={canEditPackage}
                    />
                  )}
                  <Footer
                    canSubmitPackage={canSubmitInstallPackage}
                    isLoading={isLoading}
                    isFirstSubmission={!queue}
                    canSubmitReview={canSubmitReview}
                    onSave={handleSave}
                    disabled={queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser || !canEditPackage}
                  />
                </EnhancedFormikForm>
                <HVACAttestationModal
                  title="Install Submission Confirmation"
                  label={attestationModalText}
                  values={values}
                  handleSubmit={handleSubmit}
                  isOpen={isAttestationOpen}
                  onDismiss={setAttestationSubmissionClose}
                  onAttestationConfirm={setUserAttestation}
                  isLoading={isSubmitting}
                  setSubmitting={setSubmitting}
                />
              </>
            )}
          </Formik>
        </MainContainer>
      </QueueFlagsProvider>
    </>
  );
};
